// src/layout/Header.tsx
import React from 'react';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import NotificationBell from '../features/notifications/components/NotificationBell';
import { EdgeMeetHuddleLauncher } from '../features/connect/components/EdgeMeetHuddleLauncher';
import { usePreferencesUIStore } from '@/store/preferencesUIStore';
import FirstClassClock from './FirstClassClock';
import DesktopUpdateBadge from '@/features/desktop/DesktopUpdateBadge';
import GlobalTimer from '@/features/billing/components/GlobalTimer';

interface HeaderProps {
  currentViewTitle?: string;
  isSidebarCollapsed?: boolean;
}

const Header: React.FC<HeaderProps> = ({ currentViewTitle = "Chat" }) => {
  const userInitials = "U";
  const { open: openPreferences } = usePreferencesUIStore();

  return (
    <header className="h-12 flex-shrink-0 flex items-center justify-between px-4
                     bg-[var(--lex-surface-header)]
                     text-[var(--lex-text-primary)]
                     border-b border-[var(--lex-border)]
                     transition-colors duration-300 ease-in-out
                     relative z-40">
      {/* Left side — view title (voice mode toggle lives in the chat composer) */}
      <div className="flex items-center min-w-0 flex-1 mr-4">
        <h1 className="text-lg font-semibold text-[var(--lex-text-secondary)] transition-colors duration-300 ease-in-out hidden sm:block">
          {currentViewTitle}
        </h1>
      </div>

      <FirstClassClock />

      {/* Right side — Notifications, Settings, User Profile */}
      <div className="flex items-center space-x-2 flex-shrink-0">
        <GlobalTimer />
        <DesktopUpdateBadge />
        <EdgeMeetHuddleLauncher />
        <NotificationBell />
        <button
          id="settings-menu-button"
          onClick={openPreferences}
          className="p-1.5 rounded-full text-[var(--lex-text-tertiary)] hover:bg-[var(--lex-surface-hover)] hover:text-[var(--lex-text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--lex-focus-ring)] transition-colors"
          title="Settings"
          aria-haspopup="dialog"
        >
          <Cog6ToothIcon className="h-5 w-5" />
        </button>

        {/* Rule 5 (2026-10-05): the rail's New Chat is the only way to start a
            chat. This bubble used to hard-reload /chat, which rehydrated the
            previous Lex session behind a blank pane (runs 9e4a1d8f, f6858991). */}
        <button
          onClick={openPreferences}
          className="w-7 h-7 bg-[var(--lex-accent-primary)] rounded-full flex items-center justify-center text-[var(--lex-accent-contrast)] text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-[var(--lex-surface-header)] focus:ring-[var(--lex-focus-ring)]"
          title="Account and preferences"
          aria-label="Account and preferences"
        >
          {userInitials}
        </button>
      </div>
    </header>
  );
};

export default Header;
